// src/pages/CandidateProfile.tsx: full candidate profile (managers only)
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import {
  C, Candidate, TalentJob, TalentConnection, FEEDBACK_OPTIONS, signedCVUrl,
} from '../lib/talentConnect';
import { card, inputStyle, labelStyle, btnPrimary, btnGhost, Banner, LoadingScreen, SeniorityBadge, Pills } from '../lib/talentUI';
import { MessageThread } from './CandidateDashboard';

export default function CandidateProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, firm } = useProfile();
  const [searchParams] = useSearchParams();
  const jobIdParam = searchParams.get('job') || '';

  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [jobs, setJobs]           = useState<TalentJob[]>([]);
  const [conn, setConn]           = useState<TalentConnection | null>(null);
  const [cvUrl, setCvUrl]         = useState<string | null>(null);
  const [loading, setLoading]     = useState(true);
  const [jobId, setJobId]         = useState(jobIdParam);
  const [intro, setIntro]         = useState('');
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState('');

  useEffect(() => { if (firm !== undefined) load(); /* eslint-disable-next-line */ }, [id, firm]);

  async function load() {
    setLoading(true);
    const { data: cand } = await supabase.from('candidates').select('*').eq('id', id).maybeSingle();
    setCandidate((cand as Candidate) || null);
    if (cand) setCvUrl(await signedCVUrl((cand as Candidate).cv_url));

    if (firm) {
      const [{ data: jobData }, { data: connData }] = await Promise.all([
        supabase.from('talent_jobs').select('*').eq('firm_id', firm.id).eq('status', 'active').order('created_at', { ascending: false }),
        supabase.from('talent_connections').select('*').eq('manager_firm_id', firm.id).eq('candidate_id', id),
      ]);
      const jobList = (jobData as TalentJob[]) || [];
      setJobs(jobList);
      const conns = (connData as TalentConnection[]) || [];
      const initialJob = jobList.find(j => j.id === jobIdParam)?.id || jobList[0]?.id || '';
      setJobId(prev => prev || initialJob);
      // Prefer the connection matching the chosen job, else any.
      const picked = conns.find(c => c.job_id === (jobIdParam || initialJob)) || conns[0] || null;
      setConn(picked);
    }
    setLoading(false);
  }

  // When the manager changes the target job, re-point the connection.
  useEffect(() => {
    if (!firm || !jobId) return;
    (async () => {
      const { data } = await supabase.from('talent_connections').select('*')
        .eq('manager_firm_id', firm.id).eq('candidate_id', id).eq('job_id', jobId).maybeSingle();
      setConn((data as TalentConnection) || null);
    })();
  }, [jobId, firm, id]);

  const connect = async () => {
    if (!user || !firm || !jobId) { setErr('Choose a job to connect for.'); return; }
    setBusy(true); setErr('');
    const { data, error } = await supabase.from('talent_connections').insert({
      job_id: jobId, candidate_id: id, manager_firm_id: firm.id, requested_by: user.id, status: 'pending',
    }).select().single();
    if (error) { setErr(error.message); setBusy(false); return; }
    setConn(data as TalentConnection);
    // Store the intro note as the first message (candidate sees it once accepted).
    if (intro.trim()) {
      await supabase.from('talent_messages').insert({ connection_id: (data as TalentConnection).id, sender_id: user.id, content: intro.trim() });
      setIntro('');
    }
    setBusy(false);
  };

  const setFeedback = async (fb: string) => {
    if (!conn) return;
    const { data } = await supabase.from('talent_connections').update({ manager_feedback: fb || null }).eq('id', conn.id).select().single();
    if (data) setConn(data as TalentConnection);
  };

  if (loading) return <LoadingScreen label="Loading profile…" />;
  if (!candidate) return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui' }}>
      <button onClick={() => navigate(-1)} style={{ ...btnGhost, marginBottom: '16px' }}>← Back</button>
      <Banner type="err" text="This candidate profile could not be found or is not visible to you." />
    </div>
  );

  const isManager = profile?.role === 'manager';
  const employer = !candidate.hide_employer && candidate.current_employer ? candidate.current_employer : null;
  const detail = (label: string, node: React.ReactNode) => (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ ...labelStyle, marginBottom: '6px' }}>{label}</div>
      {node}
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <button onClick={() => navigate(-1)} style={{ ...btnGhost, marginBottom: '16px' }}>← Back</button>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 360px', gap: '20px', alignItems: 'start' }}>
        {/* Left: profile */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ ...card, padding: '26px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{ width: '54px', height: '54px', borderRadius: '5px', background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gold, fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '22px', flexShrink: 0 }}>
                {(candidate.full_name || '?').charAt(0)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                  <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: C.black, fontWeight: 400, margin: 0, letterSpacing: '-0.3px' }}>{candidate.full_name || 'Candidate'}</h1>
                  <SeniorityBadge seniority={candidate.seniority} />
                </div>
                <div style={{ fontSize: '13.5px', color: C.textSec, fontWeight: 500 }}>
                  {candidate.current_role || 'Role undisclosed'}{employer ? ` · ${employer}` : ''}
                </div>
                {candidate.years_experience != null && (
                  <div style={{ fontSize: '12px', color: C.muted, marginTop: '2px' }}>{candidate.years_experience} years of experience</div>
                )}
              </div>
            </div>
          </div>

          <div style={{ ...card, padding: '24px 28px' }}>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.navy, marginBottom: '12px' }}>Overview</div>
            <p style={{ fontSize: '13.5px', color: C.textSec, lineHeight: 1.7, margin: 0 }}>{candidate.description || 'No summary provided.'}</p>
          </div>

          <div style={{ ...card, padding: '24px 28px' }}>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.navy, marginBottom: '16px' }}>Details</div>
            {(candidate.target_roles || []).length > 0 && detail('Target Roles', <Pills items={candidate.target_roles || []} />)}
            {(candidate.asset_classes || []).length > 0 && detail('Asset Classes', <Pills items={candidate.asset_classes || []} bg={`${C.teal}12`} fg={C.teal} bd={`${C.teal}33`} />)}
            {(candidate.skills || []).length > 0 && detail('Skills', <Pills items={candidate.skills || []} />)}
            {(candidate.relocation_countries || []).length > 0 && detail('Willing to Relocate To', <div style={{ fontSize: '13px', color: C.textSec }}>{(candidate.relocation_countries || []).join(', ')}</div>)}
            <div style={{ marginTop: '4px' }}>
              {cvUrl
                ? <a href={cvUrl} target="_blank" rel="noreferrer" style={{ ...btnPrimary, textDecoration: 'none', display: 'inline-block' }}>Download CV</a>
                : <span style={{ fontSize: '12.5px', color: C.mutedLt }}>No CV on file.</span>}
            </div>
          </div>
        </div>

        {/* Right: connect / messaging / feedback */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {isManager && jobs.length > 0 && (
            <div style={{ ...card, padding: '20px 22px' }}>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Connecting for role</label>
                <select value={jobId} onChange={e => setJobId(e.target.value)} style={inputStyle} disabled={!!conn}>
                  {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
                </select>
              </div>

              {err && <Banner type="err" text={err} />}

              {!conn ? (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={labelStyle}>Intro message (optional)</label>
                    <textarea rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} value={intro} onChange={e => setIntro(e.target.value)} placeholder="Tell the candidate why you're reaching out…" />
                  </div>
                  <button onClick={connect} disabled={busy || !jobId} style={{ ...btnPrimary, width: '100%', opacity: busy || !jobId ? 0.6 : 1 }}>{busy ? 'Sending…' : 'Connect'}</button>
                </>
              ) : (
                <div style={{ padding: '12px 14px', borderRadius: '3px', background: conn.status === 'accepted' ? '#F0FDF4' : conn.status === 'declined' ? '#FEF2F2' : C.surface, border: `1px solid ${conn.status === 'accepted' ? '#BBF7D0' : conn.status === 'declined' ? '#FECACA' : C.border}` }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 600, color: conn.status === 'accepted' ? C.success : conn.status === 'declined' ? C.danger : C.amber }}>
                    {conn.status === 'accepted' ? '✓ Connected' : conn.status === 'declined' ? 'Connection declined' : 'Connection pending'}
                  </div>
                  <div style={{ fontSize: '11.5px', color: C.muted, marginTop: '2px' }}>
                    {conn.status === 'pending' ? 'Waiting for the candidate to respond.' : conn.status === 'accepted' ? 'You can now message each other below.' : 'The candidate declined this request.'}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manager feedback */}
          {isManager && conn && (
            <div style={{ ...card, padding: '20px 22px' }}>
              <label style={labelStyle}>Feedback (private to you)</label>
              <select value={conn.manager_feedback || ''} onChange={e => setFeedback(e.target.value)} style={inputStyle}>
                <option value="">, Set status, </option>
                {FEEDBACK_OPTIONS.map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>)}
              </select>
            </div>
          )}

          {/* Messaging thread once accepted */}
          {isManager && conn?.status === 'accepted' && user && (
            <MessageThread connection={conn} userId={user.id} title={candidate.full_name || 'Candidate'} subtitle={jobs.find(j => j.id === conn.job_id)?.title || ''} />
          )}
        </div>
      </div>
    </div>
  );
}
